import { useState, useEffect } from "react";
import { useLazyQuery } from "@apollo/client";
import { Check, Search, X } from "lucide-react";
import { SEARCH_CLIENTS } from "../../apollo/clients";

// ── Client picker (move / merge target) ──────────────────────────────

export interface ClientPickerModalProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  /** Warning line rendered above the confirm button once a target is picked. */
  confirmNote: (targetName: string) => string;
  excludeId?: string;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (targetId: string, targetName: string) => void;
}

export function ClientPickerModal({
  open,
  title,
  confirmLabel,
  confirmNote,
  excludeId,
  busy,
  onClose,
  onConfirm,
}: ClientPickerModalProps) {
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);

  const [search, { data }] = useLazyQuery(SEARCH_CLIENTS, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (open) {
      setQuery("");
      setPicked(null);
      search({ variables: { filter: { archived: false } } });
    }
  }, [open, search]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      search({
        variables: {
          filter: query.trim()
            ? { q: query.trim(), archived: false }
            : { archived: false },
        },
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [open, query, search]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const results: { id: string; name: string; alias: string | null; peopleCount: number }[] =
    (data?.allClients ?? []).filter(
      (c: { id: string }) => c.id !== excludeId,
    );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-paper-hi rounded-lg shadow-lift w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 hair-b">
          <h2 className="font-display text-[18px] font-medium">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded hover:bg-paper-2 flex items-center justify-center text-ink-4"
            aria-label="Close"
          >
            <X size={14} strokeWidth={2} />
          </button>
        </div>
        <div className="px-6 py-4">
          <div className="relative mb-3">
            <Search size={13} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search clients..."
              autoFocus
              className="w-full pl-8 pr-3 py-2 text-[13px] rounded-md bg-canvas hair border focus:outline-none focus:border-tide text-ink placeholder:text-ink-5"
            />
          </div>
          <div className="max-h-56 overflow-y-auto divide-hair rounded-md hair border">
            {results.length === 0 ? (
              <div className="px-3 py-3 text-[12.5px] text-ink-4">No matching clients.</div>
            ) : (
              results.map((c) => {
                const label = c.alias || c.name;
                const isPicked = picked?.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setPicked({ id: c.id, name: label })}
                    className={`w-full text-left px-3 py-2.5 text-[13px] flex items-center justify-between gap-3 transition ${
                      isPicked ? "bg-tide-ss/60 text-ink" : "hover:bg-paper-2 text-ink-2"
                    }`}
                  >
                    <span className="truncate">{label}</span>
                    <span className="text-[11.5px] text-ink-4 flex-shrink-0">
                      {c.peopleCount} {c.peopleCount === 1 ? "person" : "people"}
                      {isPicked && <Check size={12} strokeWidth={2.5} className="inline ml-1.5 text-tide" />}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          {picked && (
            <p className="text-[12px] text-ink-3 mt-3">{confirmNote(picked.name)}</p>
          )}
        </div>
        <div className="px-6 py-4 hair-t flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md hair border text-[13px] text-ink-2 hover:bg-paper-2"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!picked || busy}
            onClick={() => picked && onConfirm(picked.id, picked.name)}
            className="px-4 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium hover:bg-tide-hi disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? "Working..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

